import React from 'react';
import { StandardTableActions, ColoredBadge } from '../../components/ui';
import { Link } from 'react-router-dom';
import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { SupplierInvoice } from './useData';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../config/appConfig';
import { ThemeColors } from '../../contexts/ThemeContext';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';

const formatCurrency = (amount?: number | null) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

/** Invoice total = amount - discount + VAT */
const getInvoiceTotal = (item: { amount?: number | null; discount?: number | null; vat?: number | null }) =>
  Number(item.amount ?? 0) - Number(item.discount ?? 0) + Number(item.vat ?? 0);

/** Side effect: recalc Total when amount, discount or VAT change (use setMultipleFieldValues so Total field re-renders). */
const updateTotalSideEffect = (
  _value: unknown,
  _setFieldValue: (n: string, v: unknown) => void,
  setMultipleFieldValues: (updates: Record<string, unknown>) => void,
  getFormData?: () => Record<string, unknown>
) => {
  const d = getFormData?.() ?? {};
  setMultipleFieldValues({ totalOrIncome: getInvoiceTotal(d) });
};

export type SupplierInvoiceStatusKind = 'Paid' | 'Partial' | 'Unpaid' | 'Overdue';

const SUPPLIER_INVOICE_STATUS_COLORS: Record<SupplierInvoiceStatusKind, string> = {
  Paid: '#22c55e',
  Partial: '#eab308',
  Unpaid: '#ef4444',
  Overdue: '#ef4444',
};

const getSupplierInvoicePaymentStatus = (
  amount: number,
  paymentTotal: number | null,
  dueDate?: string | null,
  formatPayment?: (amount: number) => string
): { label: string; kind: SupplierInvoiceStatusKind } => {
  if (paymentTotal != null && paymentTotal >= amount) {
    return { label: 'Paid', kind: 'Paid' };
  }
  if (paymentTotal != null && paymentTotal < amount) {
    const label = formatPayment ? `Partial (${formatPayment(paymentTotal)})` : 'Partial';
    return { label, kind: 'Partial' };
  }
  if (dueDate && dayjs(dueDate).isBefore(dayjs())) {
    return { label: 'Overdue', kind: 'Overdue' };
  }
  return { label: 'Unpaid', kind: 'Unpaid' };
};

const renderSupplierInvoiceStatusBadge = (
  label: string,
  kind: SupplierInvoiceStatusKind,
  className?: string
) => (
  <ColoredBadge label={label} color={SUPPLIER_INVOICE_STATUS_COLORS[kind]} className={className} />
);

export const moduleConfig = {
  module: {
    title: 'Supplier Invoices',
    description: 'Manage supplier invoices',
    permissionModuleKey: 'supplier_invoices',
    route: {
      list: '/supplier-invoices',
      new: '/supplier-invoices/new',
      detail: (id: number) => `/supplier-invoices/${id}`,
      edit: (id: number) => `/supplier-invoices/${id}/edit`,
    },
    api: {
      list: '/supplier-invoices',
      detail: (id: number) => `/supplier-invoices/${id}`,
    },
  },

  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  search: {
    placeholder: 'Search by supplier, invoice number, type...',
  },

  filterGroups: [
    { name: 'basic', label: 'Basic', icon: '🔍', fields: ['supplierName', 'invoiceNo', 'invoiceType', 'invoiceCategory', 'status'] },
    { name: 'financial', label: 'Financial', icon: '💰', fields: ['amount', 'discount', 'vat', 'totalOrIncome'] },
    { name: 'dates', label: 'Dates', icon: '📅', fields: ['invoiceDate', 'dueDate'] },
  ],

  filters: {
    fields: [
      { key: 'supplierName', label: 'Supplier', placeholder: 'All Suppliers', getValue: (item: SupplierInvoice) => item.supplier?.name || `#${item.supplierId}`, options: [], apiEndpoint: '/suppliers', labelField: 'name' },
      { key: 'invoiceNo', label: 'Invoice No', placeholder: 'All', getValue: (item: SupplierInvoice) => item.invoiceNo || '', options: [] },
      { key: 'invoiceType', label: 'Type', placeholder: 'All', getValue: (item: SupplierInvoice) => item.invoiceType?.pit_descr || '', options: [], apiEndpoint: '/p-crm-invoice-type', labelField: 'pit_descr' },
      { key: 'invoiceCategory', label: 'Category', placeholder: 'All', getValue: (item: SupplierInvoice) => item.invoiceCategory?.pic_descr || '', options: [], apiEndpoint: '/p-crm-invoice-category', labelField: 'pic_descr' },
      {
        key: 'status',
        label: 'Status',
        placeholder: 'All Status',
        getValue: (item: SupplierInvoice) => {
          const invoiceTotal = getInvoiceTotal(item);
          const total = item.paymentTotal != null ? Number(item.paymentTotal) : null;
          if (total == null) return 'Unpaid';
          if (total < invoiceTotal) return 'Partial';
          if (total >= invoiceTotal) return 'Paid';
          const due = item.dueDate ? dayjs(item.dueDate) : null;
          if (due && due.isBefore(dayjs())) return 'Overdue';
          return 'Unpaid';
        },
        options: ['Paid', 'Partial', 'Unpaid', 'Overdue'],
      },
      { key: 'amount', label: 'Amount', type: 'number' as const, getValue: (item: SupplierInvoice) => item.amount?.toString() ?? '', options: [] },
      { key: 'invoiceDate', label: 'Invoice Date', type: 'date' as const, getValue: (item: SupplierInvoice) => item.invoiceDate ? formatDateGlobal(item.invoiceDate) : undefined, options: [] },
      { key: 'dueDate', label: 'Due Date', type: 'date' as const, getValue: (item: SupplierInvoice) => item.dueDate ? formatDateGlobal(item.dueDate) : undefined, options: [] },
    ],
    applyFilter: (
      items: SupplierInvoice[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ): SupplierInvoice[] => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        const str = (v: unknown) => (v == null ? '' : String(v)).toLowerCase();
        if (Array.isArray(filters.supplierName) && filters.supplierName.length) {
          const name = (item.supplier?.name || `#${item.supplierId}`).toLowerCase();
          if (!filters.supplierName.some((f) => name.includes(str(f)))) return false;
        }
        if (Array.isArray(filters.invoiceNo) && filters.invoiceNo.length) {
          if (!filters.invoiceNo.some((f) => str(item.invoiceNo).includes(str(f)))) return false;
        }
        if (Array.isArray(filters.invoiceType) && filters.invoiceType.length) {
          if (!filters.invoiceType.some((f) => str(item.invoiceType?.pit_descr).includes(str(f)))) return false;
        }
        if (Array.isArray(filters.invoiceCategory) && filters.invoiceCategory.length) {
          if (!filters.invoiceCategory.some((f) => str(item.invoiceCategory?.pic_descr).includes(str(f)))) return false;
        }
        if (Array.isArray(filters.status) && filters.status.length) {
          const invoiceTotal = getInvoiceTotal(item);
          const total = item.paymentTotal != null ? Number(item.paymentTotal) : null;
          let status = 'Unpaid';
          if (total != null && total >= invoiceTotal) status = 'Paid';
          else if (total != null && total < invoiceTotal) status = 'Partial';
          else if (item.dueDate && dayjs(item.dueDate).isBefore(dayjs())) status = 'Overdue';
          if (!filters.status.includes(status)) return false;
        }
        if (Array.isArray(filters.amount) && filters.amount.length) {
          if (!filters.amount.includes(String(item.amount))) return false;
        }
        if (Array.isArray(filters.invoiceDate) && filters.invoiceDate.length && ranges.invoiceDate?.start && ranges.invoiceDate?.end && item.invoiceDate) {
          const d = new Date(item.invoiceDate);
          if (d < ranges.invoiceDate.start || d > ranges.invoiceDate.end) return false;
        }
        if (Array.isArray(filters.dueDate) && filters.dueDate.length && ranges.dueDate?.start && ranges.dueDate?.end && item.dueDate) {
          const d = new Date(item.dueDate);
          if (d < ranges.dueDate.start || d > ranges.dueDate.end) return false;
        }
        return true;
      });
    },
  },

  toolbar: {
    actions: [
      { key: 'add', icon: 'add', title: 'Add Supplier Invoice', showTitle: false, variant: 'primary' as const, href: '/supplier-invoices/new' },
    ],
  },

  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: SupplierInvoice) => { window.location.href = `/supplier-invoices/${item.id}`; },
    keyExtractor: (item: SupplierInvoice) => item.id,
  },

  emptyStates: {
    noData: 'Get started by creating a new supplier invoice.',
    noSearchResults: 'No supplier invoices found.',
    noFilterResults: 'No invoices match the selected filters.',
  },

  delete: {
    title: 'Delete Supplier Invoice',
    message: 'Are you sure you want to delete this invoice? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete invoice.',
    handler: async (id: number) => {
      const { deleteSupplierInvoice } = await import('../../services/mutations');
      await deleteSupplierInvoice(id);
    },
  },

  utils: {
    formatDate: (dateString?: string | null) => (dateString ? formatDateGlobal(dateString) : '-'),
    formatCurrency,
  },

  detail: {
    subtitle: 'Supplier Invoice Details',
    notFoundTitle: 'Invoice Not Found',
    notFoundMessage: 'The invoice you are looking for does not exist.',
    notFoundBackText: 'Back to Supplier Invoices',
    defaultTitle: 'Invoice',
    getTitle: (item: SupplierInvoice) => item.invoiceNo || `Invoice #${item.id}`,
  },

  form: {
    new: { title: 'New Supplier Invoice', subtitle: 'Create a new supplier invoice', submitText: 'Create Invoice' },
    edit: { title: 'Edit Supplier Invoice', subtitle: 'Update invoice information', submitText: 'Update Invoice' },
    cancelText: 'Cancel',
    loadingText: 'Loading invoice...',
    errorMessage: 'Failed to save invoice.',
    supportsFiles: true,
    create: async (data: Partial<SupplierInvoice>, files?: File[]) => {
      const { createSupplierInvoice } = await import('../../services/mutations');
      const payload = { ...data, totalOrIncome: getInvoiceTotal(data) };
      return await createSupplierInvoice(payload, files);
    },
    update: async (id: number, data: Partial<SupplierInvoice>, files?: File[], filesToDetach?: number[]) => {
      const { updateSupplierInvoice } = await import('../../services/mutations');
      const payload = { ...data, totalOrIncome: getInvoiceTotal(data) };
      return await updateSupplierInvoice(id, payload, files, filesToDetach);
    },
  },

  formLayout: {
    mode: 'default' as const,
    sections: [
      { id: 'basic', title: 'Basic', icon: '📄', gridCols: 'md:grid-cols-2', fields: ['supplierId', 'invoiceNo', 'invoiceDate', 'dueDate'], collapsible: true },
      { id: 'type', title: 'Type & Category', icon: '🏷️', gridCols: 'md:grid-cols-2', fields: ['invoiceTypeId', 'invoiceCategoryId'], collapsible: true },
      { id: 'financial', title: 'Financial', icon: '💰', gridCols: 'md:grid-cols-2', fields: ['amount', 'discount', 'vat', 'totalOrIncome'], collapsible: true },
      { id: 'details', title: 'Details', icon: '📝', gridCols: 'md:grid-cols-1', fields: ['description', 'notes'], collapsible: true },
    ],
  },

  fields: [
    { name: 'supplierId', label: 'Supplier', type: 'select', required: true, placeholder: 'Select supplier', apiEndpoint: '/suppliers', labelField: 'name', valueField: 'id' },
    { name: 'invoiceNo', label: 'Invoice Number', type: 'text', placeholder: 'Invoice #' },
    { name: 'invoiceDate', label: 'Invoice Date', type: 'date', placeholder: 'Date' },
    { name: 'dueDate', label: 'Due Date', type: 'date', placeholder: 'Due' },
    { name: 'invoiceTypeId', label: 'Type', type: 'select', placeholder: 'Type', apiEndpoint: '/p-crm-invoice-type', labelField: 'pit_descr', valueField: 'id' },
    { name: 'invoiceCategoryId', label: 'Category', type: 'select', placeholder: 'Category', apiEndpoint: '/p-crm-invoice-category', labelField: 'pic_descr', valueField: 'id' },
    {
      name: 'amount',
      label: 'Amount',
      type: 'number',
      placeholder: '0.00',
      prefix: <span className="text-gray-500 dark:text-gray-400">€</span>,
      step: 0.01,
      onChangeSideEffect: updateTotalSideEffect,
    },
    {
      name: 'discount',
      label: 'Discount',
      type: 'number',
      placeholder: '0.00',
      prefix: <span className="text-gray-500 dark:text-gray-400">€</span>,
      step: 0.01,
      onChangeSideEffect: updateTotalSideEffect,
    },
    {
      name: 'vat',
      label: 'VAT',
      type: 'number',
      placeholder: '0.00',
      prefix: <span className="text-gray-500 dark:text-gray-400">€</span>,
      step: 0.01,
      onChangeSideEffect: updateTotalSideEffect,
    },
    {
      name: 'totalOrIncome',
      label: 'Total',
      type: 'number',
      placeholder: '0.00',
      readOnly: true,
      prefix: <span className="text-gray-500 dark:text-gray-400">€</span>,
    },
    { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Description' },
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Notes' },
  ],
};

// Match client-invoices signature so GenericListPage 4-arg call passes listContext correctly
export const getColumns = (
  formatDate: (dateString?: string) => string,
  _formatCurrencyOptional?: unknown,
  colors?: ThemeColors,
  listContext?: { mutate?: () => void }
): Column<SupplierInvoice>[] => [
  {
    key: 'supplier.name',
    header: 'Supplier',
    sortable: true,
    render: (_, row) => <TruncatedText text={row.supplier?.name || '-'} maxLength={40} showTooltip />,
  },
  { key: 'invoiceNo', header: 'Invoice No.', sortable: true, render: (v) => v || '-' },
  {
    key: 'FileAttachment',
    header: 'Attachments',
    sortable: false,
    render: (_, row) => {
      const count = row.FileAttachment?.length ?? 0;
      return count === 0 ? <span className="text-sm text-gray-500">-</span> : <span className="text-sm">{count} file{count !== 1 ? 's' : ''}</span>;
    },
  },
  { key: 'invoiceType', header: 'Type', sortable: true, render: (_, row) => row.invoiceType?.pit_descr || '-' },
  { key: 'amount', header: 'Amount', sortable: true, render: (v) => formatCurrency(v) },
  {
    key: 'total',
    header: 'Total',
    sortable: true,
    render: (_, row) => formatCurrency(getInvoiceTotal(row)),
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (_, row) => {
      const invoiceTotal = getInvoiceTotal(row);
      const total = row.paymentTotal != null ? Number(row.paymentTotal) : null;
      const { label, kind } = getSupplierInvoicePaymentStatus(invoiceTotal, total, row.dueDate);
      const isFullyPaid = total != null && total >= invoiceTotal;
      const showPaymentBtns = !isFullyPaid && invoiceTotal > 0;

      const handleFullPayment = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const paymentDate = row.invoiceDate
          ? (typeof row.invoiceDate === 'string' ? row.invoiceDate : new Date(row.invoiceDate).toISOString())
          : new Date().toISOString();
        try {
          const { createSupplierInvoicePayment } = await import('../../services/mutations');
          await createSupplierInvoicePayment({
            supplierInvoiceId: row.id,
            paymentDate,
            paymentAmount: invoiceTotal,
          });
          listContext?.mutate?.();
          toast.success('Full payment recorded');
        } catch (err: unknown) {
          const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to record payment';
          toast.error(message);
        }
      };

      const paymentDateParam = row.invoiceDate
        ? `&paymentDate=${encodeURIComponent(typeof row.invoiceDate === 'string' ? row.invoiceDate : new Date(row.invoiceDate).toISOString())}`
        : '';
      const newPaymentUrl = `/supplier-invoice-payments/new?supplierInvoiceId=${row.id}${paymentDateParam}`;

      return (
        <div className="flex items-center gap-1.5 flex-wrap">
          {renderSupplierInvoiceStatusBadge(label, kind)}
          {showPaymentBtns && (
            <>
              <button
                type="button"
                onClick={handleFullPayment}
                className="inline-flex items-center justify-center p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded transition-colors"
                title="Record full payment"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <Link
                to={newPaymentUrl}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center justify-center p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors"
                title="Record partial payment"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </Link>
            </>
          )}
        </div>
      );
    },
  },
  { key: 'invoiceDate', header: 'Invoice Date', sortable: true, render: (_, row) => formatDate(row.invoiceDate) },
  {
    key: 'dueDate',
    header: 'Due Date',
    sortable: true,
    render: (value, row) => {
      const invoiceTotal = getInvoiceTotal(row);
      const total = row.paymentTotal != null ? Number(row.paymentTotal) : null;
      const isFullyPaid = total != null && total >= invoiceTotal;
      const due = value ? dayjs(value) : null;
      const isOverdue = !isFullyPaid && due && due.isBefore(dayjs());
      const days = isOverdue && due ? dayjs().diff(due, 'day') : null;
      return (
        <div>
          <span className={isOverdue ? 'text-red-600 font-medium' : ''}>{formatDate(value)}</span>
          {days != null && days > 0 && <div className="text-xs text-red-600">{days} days overdue</div>}
        </div>
      );
    },
  },
];

export const getDetailSections = (item: SupplierInvoice, formatDate: (dateString?: string) => string) => {
  const invoiceTotal = getInvoiceTotal(item);
  const paymentTotal = item.paymentTotal != null ? Number(item.paymentTotal) : null;
  const { label, kind } = getSupplierInvoicePaymentStatus(invoiceTotal, paymentTotal, item.dueDate, formatCurrency);

  return [
    {
      title: 'Basic Information',
      icon: '📄',
      fields: [
        { label: 'Supplier', value: item.supplier?.name || `#${item.supplierId}` || '-' },
        { label: 'Invoice Number', value: item.invoiceNo || '-' },
        { label: 'Invoice Date', value: formatDate(item.invoiceDate) },
        { label: 'Due Date', value: formatDate(item.dueDate) },
        { label: 'Status', value: renderSupplierInvoiceStatusBadge(label, kind) },
      ],
    },
    {
      title: 'Type & Category',
      icon: '🏷️',
      fields: [
        { label: 'Type', value: item.invoiceType?.pit_descr || '-' },
        { label: 'Category', value: item.invoiceCategory?.pic_descr || '-' },
      ],
    },
    {
      title: 'Financial',
      icon: '💰',
      fields: [
        { label: 'Amount', value: formatCurrency(item.amount) },
        { label: 'Discount', value: item.discount != null ? `${item.discount}` : '-' },
        { label: 'VAT', value: formatCurrency(item.vat) },
        { label: 'Total', value: formatCurrency(getInvoiceTotal(item)) },
        { label: 'Payment Total', value: formatCurrency(paymentTotal) },
      ],
    },
    {
      title: 'Details',
      icon: '📝',
      fields: [
        { label: 'Description', value: item.description || '-' },
        { label: 'Notes', value: item.notes || '-' },
      ],
    },
    ...(item.FileAttachment && item.FileAttachment.length > 0
      ? [{
          title: 'Attachments',
          icon: '📎',
          fields: item.FileAttachment.map((f) => ({ label: f.fileName || 'File', value: f.fileName || f.filePath || '-' })),
        }]
      : []),
  ];
};

export const getStatusBadges = (item: SupplierInvoice, _colors?: ThemeColors) => {
  const invoiceTotal = getInvoiceTotal(item);
  const total = item.paymentTotal != null ? Number(item.paymentTotal) : null;
  const { label, kind } = getSupplierInvoicePaymentStatus(invoiceTotal, total, item.dueDate);
  return renderSupplierInvoiceStatusBadge(label, kind, 'px-3 py-1 text-sm font-semibold');
};

export const getInitialFormData = (): Partial<SupplierInvoice> => ({
  supplierId: undefined as unknown as number,
  invoiceTypeId: undefined,
  invoiceCategoryId: undefined,
  invoiceDate: undefined,
  dueDate: undefined,
  invoiceNo: '',
  description: '',
  amount: undefined,
  discount: undefined,
  vat: undefined,
  totalOrIncome: undefined,
  notes: '',
});

export const mapItemToFormData = (item: SupplierInvoice): Partial<SupplierInvoice> => ({
  supplierId: item.supplierId,
  invoiceTypeId: item.invoiceTypeId,
  invoiceCategoryId: item.invoiceCategoryId,
  invoiceDate: item.invoiceDate,
  dueDate: item.dueDate,
  invoiceNo: item.invoiceNo || '',
  description: item.description || '',
  amount: item.amount,
  discount: item.discount,
  vat: item.vat,
  totalOrIncome: getInvoiceTotal(item),
  notes: item.notes || '',
});

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add')
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  return null;
};

const handleDuplicate = async (item: SupplierInvoice) => {
  try {
    const payload = mapItemToFormData(item);
    const invoiceNo = (item.invoiceNo || '').trim();
    const duplicatePayload = {
      ...payload,
      invoiceNo: invoiceNo ? `${invoiceNo} (copy)` : 'Copy',
    };
    const { createSupplierInvoice } = await import('../../services/mutations');
    const created = await createSupplierInvoice(duplicatePayload);
    const newId = (created as { id?: number })?.id;
    if (typeof newId === 'number') {
      window.location.href = moduleConfig.module.route.edit(newId);
    } else {
      window.location.href = moduleConfig.module.route.list;
    }
  } catch (err) {
    console.error('Failed to duplicate supplier invoice:', err);
    toast.error('Failed to duplicate invoice');
  }
};

export const getTableActions = (
  item: SupplierInvoice,
  onDelete: (id: number) => void,
  _colors?: unknown,
  _listContext?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <StandardTableActions
      viewTo={moduleConfig.module.route.detail(item.id)}
      editTo={moduleConfig.module.route.edit(item.id)}
      onDelete={() => onDelete(item.id)}
      canEdit={canEdit}
      canDelete={canDelete}
    />
  );
};

